import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Button,
  InputAdornment,
  IconButton,
  Paper,
  Typography,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { IMaskInput } from 'react-imask';

import { useSearchBuyer } from './hooks/useSearchBuyer';
import { useCreateBuyer } from './hooks/useCreateBuyer';
import AlertDialog from '../../../../components/other/Dialog/AlertDialog';
import { formatPhone } from '../../../../utils/utils';

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

const TextMaskCustom = React.forwardRef<HTMLInputElement, CustomProps>((props, ref) => {
  const { onChange, ...other } = props;
  return (
    <IMaskInput
      {...other}
      mask='0 000 000-00-00'
      definitions={{
        '0': /[0-9]/,
      }}
      inputRef={ref}
      overwrite
      onAccept={(value: any) => onChange({ target: { name: props.name, value } })}
    />
  );
});

export function AutocompleteBuyer({ formik }: { formik: any }) {
  const { values, handleBlur, errors, setFieldValue } = formik;
  const [number, setNumber] = useState('');
  const [createUserPhone, setCreateUserPhone] = useState('');
  const { fetchUsers, users, setIsEmpty, isEmpty } = useSearchBuyer();
  const { createdUser, user } = useCreateBuyer();
  const [focus, setFocus] = useState(false);

  const handleFindUser = () => {
    if (number.length === 11) {
      fetchUsers({ phone: number });
    }
  };

  const handleSubmit = () => {
    createdUser({
      roles: ['ROLE_BUYER'],
      countryCode: '7',
      phoneNumbers: number.slice(1),
    });
  };

  useEffect(() => {
    if (user) {
      setFieldValue('user', user);
      setIsEmpty(false);
      setCreateUserPhone('');
    }
  }, [user]);

  useEffect(() => {
    if (values.user) {
      setNumber(values.user.phone);
    }
  }, [values.user]);

  return (
    <>
      <Autocomplete
        options={users}
        noOptionsText={false}
        filterOptions={o => o}
        PaperComponent={({ children }) => (
          <Paper>
            {isEmpty && users.length === 0 && number.length === 11 && (
              <Typography
                style={{ cursor: 'pointer' }}
                color='#FF8B00'
                padding='10px'
                component='p'
                onMouseDown={() => {
                  setCreateUserPhone(number);
                }}
              >
                Заказчик не найден. Зарегистрировать нового?
              </Typography>
            )}
            {children}
          </Paper>
        )}
        getOptionLabel={option => {
          return `${formatPhone(option.phone || '')}`;
        }}
        open={(users.length > 0 || isEmpty) && focus}
        onChange={(e, value) => {
          if (value) {
            setFieldValue('user', value);
          } else {
            setFieldValue('user', null);
          }
        }}
        inputValue={number}
        onOpen={() => {
          setFocus(true);
        }}
        onClose={() => {
          setFocus(false);
        }}
        disableClearable
        renderInput={(params: any) => (
          <TextField
            {...params}
            name='phone'
            label='Заказчик'
            margin='normal'
            variant='outlined'
            value={number}
            onBlur={handleBlur}
            onChange={e => {
              setNumber(e.target.value.replace(/\D/g, ''));
              setIsEmpty(false);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleFindUser();
              }
            }}
            helperText={errors.user?.id || errors.user}
            error={Boolean(errors.user?.id || errors.user)}
            InputProps={{
              ...params.InputProps,
              style: { height: 68 },
              inputComponent: TextMaskCustom as any,
              startAdornment: (
                <InputAdornment style={{ paddingTop: 2 }} position='start'>
                  +
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {values.user && (
                    <IconButton
                      onClick={() => {
                        setFieldValue('user', null);
                        setNumber('');
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                  {number.length === 11 && <Button onClick={handleFindUser}>Найти</Button>}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      <AlertDialog
        open={!!createUserPhone}
        message={`Заказчик с номером ${formatPhone(`${createUserPhone}`)} не найден. Зарегистрировать нового заказчика?`}
        okText='Добавить'
        cancelText='Отмена'
        handleClose={() => {
          setCreateUserPhone('');
        }}
        handleAgree={() => handleSubmit()}
      />
    </>
  );
}
